/**
 * Partner Service
 *
 * Abstracts data fetching for partner configurations. Uses real API calls.
 *
 * Follows Interface Segregation and Dependency Inversion principles.
 */

import type {
  PartnerResponse,
  PartnerListResponse,
  PartnerQueryParams,
  CreatePartnerRequest,
  UpdatePartnerRequest,
  BulkAssignOfferRequest,
  BulkAssignOfferResponse,
  BulkRemoveOfferRequest,
  BulkRemoveOfferResponse,
  BulkReplaceOfferRequest,
  BulkReplaceOfferResponse,
} from '@/app/types/partners';
import { api } from '@/app/services/apiClient';
import { API_ENDPOINTS } from '@/app/config/api';

/**
 * Partner Service Interface
 * Define the contract that any implementation must follow
 */
export interface IPartnerService {
  queryPartners(params: PartnerQueryParams): Promise<PartnerListResponse>;
  getPartnerById(id: number): Promise<PartnerResponse | null>;
  createPartner(request: CreatePartnerRequest): Promise<PartnerResponse>;
  updatePartner(id: number, request: UpdatePartnerRequest): Promise<PartnerResponse | null>;
  deactivatePartner(id: number): Promise<PartnerResponse | null>;
  activatePartner(id: number): Promise<PartnerResponse | null>;
  /** Bulk assign an offer to all users of a specific partner */
  bulkAssignOfferToPartner(
    partnerId: number,
    request: BulkAssignOfferRequest
  ): Promise<BulkAssignOfferResponse>;
  /** Bulk assign an offer to all users of ALL active partners */
  bulkAssignOfferToAllPartners(request: BulkAssignOfferRequest): Promise<BulkAssignOfferResponse>;
  /** Bulk remove an offer from all users of a specific partner */
  bulkRemoveOfferFromPartner(
    partnerId: number,
    request: BulkRemoveOfferRequest
  ): Promise<BulkRemoveOfferResponse>;
  /** Bulk remove an offer from all users of ALL active partners */
  bulkRemoveOfferFromAllPartners(request: BulkRemoveOfferRequest): Promise<BulkRemoveOfferResponse>;
  /** Bulk replace one reward with another for all users of a specific partner */
  bulkReplaceOfferForPartner(
    partnerId: number,
    request: BulkReplaceOfferRequest
  ): Promise<BulkReplaceOfferResponse>;
  /** Bulk replace one reward with another for all users of ALL active partners */
  bulkReplaceOfferForAllPartners(
    request: BulkReplaceOfferRequest
  ): Promise<BulkReplaceOfferResponse>;
}

/**
 * Real API Implementation
 */
const apiPartnerService: IPartnerService = {
  async queryPartners(params: PartnerQueryParams): Promise<PartnerListResponse> {
    try {
      // Build query params, filtering out undefined/null values
      const queryParams: Record<string, any> = {};
      if (params.partner_type !== undefined && params.partner_type !== null) {
        queryParams.partner_type = params.partner_type;
      }
      if (params.is_active !== undefined && params.is_active !== null) {
        queryParams.is_active = params.is_active;
      }
      if (params.search !== undefined && params.search !== null) {
        queryParams.search = params.search;
      }
      if (params.limit !== undefined && params.limit !== null) {
        queryParams.limit = params.limit;
      }
      if (params.offset !== undefined && params.offset !== null) {
        queryParams.offset = params.offset;
      }

      const response = await api.get<PartnerListResponse>(API_ENDPOINTS.admin.partners, {
        params: queryParams,
      });
      return response.data;
    } catch (error: any) {
      console.error('Failed to query partners:', error);
      throw error;
    }
  },

  async getPartnerById(id: number): Promise<PartnerResponse | null> {
    try {
      const response = await api.get<PartnerResponse>(`${API_ENDPOINTS.admin.partners}/${id}`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      console.error('Failed to get partner:', error);
      throw error;
    }
  },

  async createPartner(request: CreatePartnerRequest): Promise<PartnerResponse> {
    try {
      const response = await api.post<PartnerResponse>(API_ENDPOINTS.admin.partners, request);
      return response.data;
    } catch (error: any) {
      console.error('Failed to create partner:', error);
      throw error;
    }
  },

  async updatePartner(id: number, request: UpdatePartnerRequest): Promise<PartnerResponse | null> {
    try {
      const response = await api.put<PartnerResponse>(
        `${API_ENDPOINTS.admin.partners}/${id}`,
        request
      );
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      console.error('Failed to update partner:', error);
      throw error;
    }
  },

  async deactivatePartner(id: number): Promise<PartnerResponse | null> {
    try {
      const response = await api.post<PartnerResponse>(
        `${API_ENDPOINTS.admin.partners}/${id}/deactivate`
      );
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      console.error('Failed to deactivate partner:', error);
      throw error;
    }
  },

  async activatePartner(id: number): Promise<PartnerResponse | null> {
    try {
      const response = await api.post<PartnerResponse>(
        `${API_ENDPOINTS.admin.partners}/${id}/activate`
      );
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      console.error('Failed to activate partner:', error);
      throw error;
    }
  },

  async bulkAssignOfferToPartner(
    partnerId: number,
    request: BulkAssignOfferRequest
  ): Promise<BulkAssignOfferResponse> {
    try {
      const response = await api.post<BulkAssignOfferResponse>(
        API_ENDPOINTS.admin.partnerBulkOffers(partnerId),
        request
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to bulk assign offer to partner:', error);
      throw error;
    }
  },

  async bulkAssignOfferToAllPartners(
    request: BulkAssignOfferRequest
  ): Promise<BulkAssignOfferResponse> {
    try {
      const response = await api.post<BulkAssignOfferResponse>(
        API_ENDPOINTS.admin.partnersBulkOffers,
        request
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to bulk assign offer to all partners:', error);
      throw error;
    }
  },

  async bulkRemoveOfferFromPartner(
    partnerId: number,
    request: BulkRemoveOfferRequest
  ): Promise<BulkRemoveOfferResponse> {
    try {
      const response = await api.post<BulkRemoveOfferResponse>(
        API_ENDPOINTS.admin.partnerBulkRemove(partnerId),
        request
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to bulk remove offer from partner:', error);
      throw error;
    }
  },

  async bulkRemoveOfferFromAllPartners(
    request: BulkRemoveOfferRequest
  ): Promise<BulkRemoveOfferResponse> {
    try {
      const response = await api.post<BulkRemoveOfferResponse>(
        API_ENDPOINTS.admin.partnersBulkRemove,
        request
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to bulk remove offer from all partners:', error);
      throw error;
    }
  },

  async bulkReplaceOfferForPartner(
    partnerId: number,
    request: BulkReplaceOfferRequest
  ): Promise<BulkReplaceOfferResponse> {
    try {
      const response = await api.post<BulkReplaceOfferResponse>(
        API_ENDPOINTS.admin.partnerBulkReplace(partnerId),
        request
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to bulk replace offer for partner:', error);
      throw error;
    }
  },

  async bulkReplaceOfferForAllPartners(
    request: BulkReplaceOfferRequest
  ): Promise<BulkReplaceOfferResponse> {
    try {
      const response = await api.post<BulkReplaceOfferResponse>(
        API_ENDPOINTS.admin.partnersBulkReplace,
        request
      );
      return response.data;
    } catch (error: any) {
      console.error('Failed to bulk replace offer for all partners:', error);
      throw error;
    }
  },
};

// Export the active implementation
// Switch between mock and real by changing this export
export const partnerService: IPartnerService = apiPartnerService;

// Also export as default for easier imports
export default partnerService;
